import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const IntegrationStatusPanel = ({ integrations, onSync, onViewDetails }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'synced':
        return 'text-success bg-success/10';
      case 'syncing':
        return 'text-warning bg-warning/10';
      case 'error':
        return 'text-error bg-error/10';
      case 'pending':
        return 'text-muted-foreground bg-muted';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'synced':
        return 'CheckCircle';
      case 'syncing':
        return 'Loader';
      case 'error':
        return 'AlertCircle';
      case 'pending':
        return 'Clock';
      default:
        return 'Circle';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'synced':
        return 'Synchronisiert';
      case 'syncing':
        return 'Wird synchronisiert';
      case 'error':
        return 'Fehler';
      case 'pending':
        return 'Ausstehend';
      default:
        return 'Unbekannt';
    }
  };

  return (
    <div className="bg-muted/30 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon name="Database" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Systemintegrationen</h3>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName="RefreshCw"
          iconPosition="left"
          onClick={() => onSync('all')}
        >
          Alle synchronisieren
        </Button>
      </div>
      <div className="space-y-3">
        {integrations?.map((integration) => (
          <div
            key={integration?.id}
            className="p-4 bg-background rounded-lg border border-border hover:border-primary/50 transition-colors"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1">
                <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name={integration?.icon} size={20} className="text-primary" />
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-sm font-medium text-foreground">
                      {integration?.name}
                    </h4>
                    <div className={`px-2 py-0.5 rounded-full flex items-center gap-1 ${getStatusColor(integration?.status)}`}>
                      <Icon 
                        name={getStatusIcon(integration?.status)} 
                        size={12} 
                        className={integration?.status === 'syncing' ? 'animate-spin' : ''}
                      />
                      <span className="text-xs font-medium">
                        {getStatusText(integration?.status)}
                      </span>
                    </div>
                  </div>

                  <p className="text-xs text-muted-foreground mb-2">
                    {integration?.description}
                  </p>

                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <span className="text-muted-foreground">Letzte Synchronisation:</span>
                      <p className="text-foreground font-medium">{integration?.lastSync}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Datensätze:</span>
                      <p className="text-foreground font-medium">{integration?.recordCount}</p>
                    </div>
                  </div>

                  {integration?.status === 'error' && integration?.errorMessage && (
                    <div className="mt-2 p-2 bg-error/5 rounded border border-error/20">
                      <p className="text-xs text-error flex items-start gap-1">
                        <Icon name="AlertCircle" size={12} className="mt-0.5 flex-shrink-0" />
                        {integration?.errorMessage}
                      </p>
                    </div>
                  )}

                  {integration?.pendingChanges > 0 && (
                    <div className="mt-2 flex items-center gap-1 text-xs text-warning">
                      <Icon name="AlertTriangle" size={12} />
                      <span>{integration?.pendingChanges} ausstehende Änderungen</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-2 ml-3">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="RefreshCw"
                  onClick={() => onSync(integration?.id)}
                  disabled={integration?.status === 'syncing'}
                >
                  Sync
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="ExternalLink"
                  onClick={() => onViewDetails(integration?.id)}
                >
                  Details
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {integrations?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="Database" size={48} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            Keine Integrationen konfiguriert
          </p>
        </div>
      )}
      <div className="mt-4 flex items-start gap-2 p-3 bg-accent/5 rounded-lg border border-accent/20">
        <Icon name="Shield" size={16} className="text-accent mt-0.5 flex-shrink-0" />
        <p className="text-xs text-muted-foreground">
          Alle Datenübertragungen sind verschlüsselt und erfolgen über sichere Verbindungen. 
          Synchronisierungen werden automatisch alle 15 Minuten durchgeführt.
        </p>
      </div>
    </div>
  );
};

export default IntegrationStatusPanel;